// Element mutators — pure (state, args) => patch functions applied via
// editor.apply(). No DOM, no Konva, no store access: trivially unit-testable,
// and every one takes id arrays so multi-select edits come for free.
// Return null for a no-op (editor skips the undo entry).
//
// Locked elements (`data.locked`, e.g. a traced-over site photo) can't be
// selected, so they rarely reach these — but every mutator that edits by id
// also skips them, so no path (stray banner, select-all, a stale selection)
// can move, delete or restack one.

import { shiftCoords, scaleCoord, isBoxType } from '../bounds';

export function isLocked(el) { return !!el?.data?.locked; }

// The subset of `ids` naming elements that exist and aren't locked.
export function unlockedIds(state, ids) {
  const requested = new Set(ids);
  return state.elements.filter((el) => requested.has(el.id) && !isLocked(el)).map((el) => el.id);
}

export function deleteElements(state, { ids }) {
  const set = new Set(unlockedIds(state, ids));
  if (!set.size) return null;
  const elements = state.elements.filter((el) => !set.has(el.id));
  if (elements.length === state.elements.length) return null;
  return { elements, selection: [] };
}

// Append pre-built elements (ids already assigned by the caller — id generation
// isn't deterministic, so it stays out of the mutator).
export function addElements(state, { elements, select = true }) {
  if (!elements?.length) return null;
  const patch = { elements: [...state.elements, ...elements] };
  if (select) patch.selection = elements.map((el) => el.id);
  return patch;
}

export function nudgeElements(state, { ids, dx, dy }) {
  const set = new Set(unlockedIds(state, ids));
  if (!set.size || (!dx && !dy)) return null;
  return {
    elements: state.elements.map((el) => {
      if (!set.has(el.id)) return el;
      const d = el.data;
      if (isBoxType(el.type)) return { ...el, data: { ...d, x: d.x + dx, y: d.y + dy } };
      if (Array.isArray(d.coordinates)) return { ...el, data: { ...d, coordinates: shiftCoords(d.coordinates, dx, dy) } };
      return el;
    }),
  };
}

// Z-order: 'front' | 'back' | 'forward' | 'backward' over the selection.
export function reorderElements(state, { ids, dir }) {
  const sel = new Set(unlockedIds(state, ids));
  if (!sel.size) return null;
  let els = state.elements.slice();
  if (dir === 'front') els = [...els.filter((e) => !sel.has(e.id)), ...els.filter((e) => sel.has(e.id))];
  else if (dir === 'back') els = [...els.filter((e) => sel.has(e.id)), ...els.filter((e) => !sel.has(e.id))];
  else {
    const step = dir === 'forward' ? 1 : -1;
    const idxs = els.map((e, i) => [e, i]).filter(([e]) => sel.has(e.id)).map(([, i]) => i);
    for (const i of (step > 0 ? idxs.slice().reverse() : idxs)) {
      const j = i + step;
      if (j < 0 || j >= els.length || sel.has(els[j].id)) continue;
      [els[i], els[j]] = [els[j], els[i]];
    }
  }
  return { elements: els };
}

function validFactor(factor) { return isFinite(factor) && factor > 0 && Math.abs(factor - 1) >= 1e-6; }

// Scale one element's geometry about `origin`. Box sizes that are still null
// (an image not yet sized, drawn at its natural size) stay null.
function scaleElement(el, factor, origin) {
  const d = el.data;
  if (isBoxType(el.type)) {
    return {
      ...el,
      data: {
        ...d,
        x: origin.x + ((d.x || 0) - origin.x) * factor,
        y: origin.y + ((d.y || 0) - origin.y) * factor,
        width: el.type === 'image' && d.width == null ? null : (d.width || 0) * factor,
        ...(d.height != null ? { height: d.height * factor } : {}),
      },
    };
  }
  if (Array.isArray(d.coordinates)) {
    return { ...el, data: { ...d, coordinates: d.coordinates.map((c) => scaleCoord(c, origin, factor)) } };
  }
  return el;
}

// Scale every element (images and locked elements included) about `origin` —
// calibration makes the whole plan read true in real-world units.
export function scalePlan(state, { factor, origin }) {
  if (!validFactor(factor)) return null;
  return { elements: state.elements.map((el) => scaleElement(el, factor, origin)) };
}

// Scale ONLY the image elements about `origin` — calibrating a photo against a
// plan that's already drawn to scale (v1's SCALE_IMAGE): the drawing stays put.
export function scaleImages(state, { factor, origin }) {
  if (!validFactor(factor) || !state.elements.some((el) => el.type === 'image')) return null;
  return { elements: state.elements.map((el) => (el.type === 'image' ? scaleElement(el, factor, origin) : el)) };
}

// Show or hide every image element at once (Alt+I).
export function setImagesVisible(state, { visible }) {
  let hit = false;
  const elements = state.elements.map((el) => {
    if (el.type !== 'image' || (el.data?.visible !== false) === visible) return el;
    hit = true;
    return { ...el, data: { ...el.data, visible } };
  });
  return hit ? { elements } : null;
}

// Lock or unlock one element. A newly locked element also leaves the selection
// (locked elements can't be selected).
export function toggleLock(state, { id }) {
  const el = state.elements.find((e) => e.id === id);
  if (!el) return null;
  const locked = !isLocked(el);
  return {
    elements: state.elements.map((e) => (e === el ? { ...e, data: { ...e.data, locked } } : e)),
    selection: state.selection.filter((sid) => sid !== id),
  };
}
